import { spawn, type ChildProcess } from 'node:child_process';

const isWindows = process.platform === 'win32';

export interface DevCommand {
  cmd: string;
  args: string[];
  /** Required for .cmd shims (npm) on Windows; never used with user input. */
  needsShell: boolean;
}

/** `npm run dev` — node backends and the Vite frontend. */
export function npmRunDev(): DevCommand {
  // Windows: npm is npm.cmd — since the CVE-2024-27980 hardening, .cmd
  // files can only be spawned through a shell. Command + args are
  // compile-time constants, so shell:true carries no injection surface.
  return { cmd: 'npm', args: ['run', 'dev'], needsShell: isWindows };
}

/** `python -m src.main` — the python backend template's entrypoint. */
export function pythonDev(): DevCommand {
  return { cmd: isWindows ? 'python' : 'python3', args: ['-m', 'src.main'], needsShell: false };
}

/**
 * Spawn a dev server, cross-platform. POSIX gets a detached process group
 * so the whole tree (npm → node → vite…) can be signalled at once.
 */
export function spawnDevServer(command: DevCommand, cwd: string): ChildProcess {
  return spawn(command.cmd, command.args, {
    cwd,
    stdio: ['ignore', 'pipe', 'pipe'],
    env: { ...process.env },
    shell: command.needsShell,
    detached: !isWindows,
  });
}

/** Kill a dev-server process tree, cross-platform. */
export function killTree(proc: ChildProcess): void {
  if (!proc.pid) return;
  if (isWindows) {
    // taskkill /T takes the whole tree (npm → node → vite etc.).
    spawn('taskkill', ['/pid', String(proc.pid), '/T', '/F'], {
      stdio: 'ignore',
      shell: false,
    }).on('error', () => proc.kill());
    return;
  }
  try {
    // Negative pid = the detached process group created in spawnDevServer.
    process.kill(-proc.pid, 'SIGTERM');
  } catch {
    try {
      proc.kill('SIGTERM');
    } catch {
      /* already gone */
    }
  }
}

/**
 * Extract the local URL a dev server announced. Vite prints
 * "Local: http://localhost:5173/"; the backend templates log
 * "Server listening on port 8080" (no URL), so a bare port mention counts.
 */
export function detectLocalUrl(line: string): string | null {
  const url = line.match(/https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?(?:\/\S*)?/i);
  if (url) return url[0].replace(/[.,;)\]]+$/, '');
  const port = line.match(/listening on port[:\s]+(\d{2,5})/i);
  if (port) return `http://localhost:${port[1]}`;
  return null;
}

/** Poll a URL until it responds (any HTTP status counts as "up"). */
export async function waitForHttp(url: string, timeoutMs: number): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    try {
      await fetch(url, { signal: AbortSignal.timeout(2000) });
      return true;
    } catch {
      await new Promise((r) => setTimeout(r, 500));
    }
  }
  return false;
}
